import CurrencyInput from './CurrencyInput.jsx';
import { maxLengthFor, isOverLength, isNonInteger } from '../schema/fieldLimits.js';
import Combobox from './Combobox.jsx';
import {
  isDateOutOfRange, getDateRange, getDateRangeHint,
  isNumberOutOfRange, isEndBeforeStart, sanitizeNumericInput, isNotNumeric,
} from '../utils/validation.js';

export default function TableCell({ column, value, onChange, readOnly = false, row = {}, ariaLabel }) {
  const v = value ?? '';

  if (column.hidden) {
    return <input type="hidden" value={v} readOnly />;
  }

  // A column that only applies to some rows (the term on a term life policy)
  // is greyed out on the others rather than hidden, so the columns line up and
  // the advisor can see the question exists and why it is closed.
  if (column.appliesWhen && !column.appliesWhen(row)) {
    return (
      <input type="text" value="" disabled placeholder="—" aria-label={ariaLabel} title={column.appliesHint} />
    );
  }

  const handle = readOnly ? () => {} : onChange;
  // ariaLabel names the otherwise-unlabeled cell input for assistive tech
  // ("Market Value, Primary Residence"); provided by DataTable.
  // Mirror the Salesforce column length so an over-length value cannot be
  // typed. A value already over the cap (loaded from the record, or entered
  // before this guard) is flagged instead, since maxLength does not shorten
  // what is already there and that value is what wedges the whole save.
  const maxLen = maxLengthFor(column);
  const tooLong = isOverLength(v, maxLen);
  // Columns marked `numeric` hold a number but render as text, because
  // Salesforce stores them in a length-capped text field (Term, Year Acquired).
  // The payload still sends them as numbers, so a stray character is withheld —
  // and without this flag that withholding is silent, which is how a percent
  // sign in a detail column cost an advisor a morning on 2026-09-18. The typed
  // columns (percent, currency) strip the character on the way in instead.
  const badNumber = !readOnly && column.numeric === true && isNotNumeric(v);
  const inputProps = {
    'aria-label': ariaLabel,
    ...(maxLen ? { maxLength: maxLen } : {}),
    ...(tooLong
      ? { className: 'date-invalid', 'aria-invalid': true,
          title: `Must be ${maxLen} characters or fewer, or this will not save.` }
      : badNumber
      ? { className: 'date-invalid', 'aria-invalid': true,
          title: 'Enter digits only. Anything else in this field will not save.' }
      : {}),
    ...(readOnly ? { readOnly: true } : {}),
  };

  switch (column.type) {
    case 'select':
      return (
        <select
          aria-label={ariaLabel}
          value={v}
          onChange={e => handle(e.target.value)}
          disabled={readOnly}
        >
          <option value="">—</option>
          {(column.options || []).map(o => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      );

    case 'checkbox':
      // Unticked is stored blank rather than false, so it reads as an empty
      // field everywhere a row is checked for data or diffed for the change log.
      return (
        <input
          type="checkbox"
          className="cell-checkbox"
          aria-label={ariaLabel}
          checked={value === true || value === 'true'}
          disabled={readOnly}
          onChange={e => handle(e.target.checked ? true : '')}
        />
      );

    case 'currency':
      return <CurrencyInput value={v} onChange={handle} {...inputProps} />;

    case 'percent': {
      // Percentages default to 0–100; a column may widen the cap (e.g. COLA).
      const min = column.min ?? 0;
      const max = column.max ?? 100;
      const invalid = !readOnly && isNumberOutOfRange(v, min, max);
      return (
        <span className="pct-cell" style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
          <input
            type="text"
            inputMode="decimal"
            className={invalid ? 'date-invalid' : undefined}
            title={invalid ? `Enter a percentage between ${min} and ${max}.` : undefined}
            value={v}
            onChange={e => handle(sanitizeNumericInput(e.target.value))}
            aria-invalid={invalid || undefined}
            {...inputProps}
          />
          <span aria-hidden="true" style={{ color: 'var(--ink-4)' }}>%</span>
        </span>
      );
    }

    case 'datalist':
      return (
        <Combobox
          value={v}
          onChange={handle}
          options={column.options || []}
          readOnly={readOnly}
          ariaLabel={ariaLabel}
          maxLength={maxLen}
          invalid={tooLong}
        />
      );

    case 'date': {
      const { min, max } = getDateRange(column.dateRange);
      const rangeInvalid = !readOnly && isDateOutOfRange(v, min, max);
      // Cross-field: an end date must not precede its start date (column
      // declares the partner field via `notBefore`).
      const orderInvalid =
        !readOnly && column.notBefore && isEndBeforeStart(row[column.notBefore], v);
      const invalid = rangeInvalid || orderInvalid;
      const title = orderInvalid
        ? 'End date must be on or after the start date.'
        : rangeInvalid ? getDateRangeHint(column.dateRange) : undefined;
      return (
        <input
          type="date"
          className={invalid ? 'date-invalid' : undefined}
          title={title}
          min={min}
          max={max}
          value={v}
          onChange={e => handle(e.target.value)}
          aria-invalid={invalid || undefined}
          {...inputProps}
        />
      );
    }

    case 'number': {
      // A fraction on a whole-number Salesforce field is refused in the Apex
      // DESERIALIZER, before field validation, so it takes the entire record
      // down and reports only a byte offset. Flagged here because the value is
      // meaningful to the advisor ("66 and 4 months") and only they can decide
      // what to put instead.
      const fraction = !readOnly && column.integer && isNonInteger(v);
      const outOfRange = !readOnly && isNumberOutOfRange(v, column.min, column.max);
      const invalid = outOfRange || fraction;
      const bounds = [column.min, column.max];
      return (
        <input
          type="number"
          className={invalid ? 'date-invalid' : undefined}
          step={column.integer ? 1 : undefined}
          title={fraction
            ? 'Whole numbers only — this will not save with a decimal.'
            : outOfRange && bounds.some(b => b != null)
              ? `Enter a value between ${column.min ?? '–∞'} and ${column.max ?? '∞'}.`
              : undefined}
          min={column.min}
          max={column.max}
          value={v}
          onChange={e => handle(e.target.value)}
          aria-invalid={invalid || undefined}
          {...inputProps}
        />
      );
    }

    default:
      return (
        <input
          type="text"
          value={v}
          onChange={e => handle(e.target.value)}
          {...inputProps}
        />
      );
  }
}
